// @flow
import api from '../api/sellerDetail';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_SELLER_DETAILS_FAIL = 'la/domain/sellerDetail/LOAD_FAIL';
export const LOAD_SELLER_DETAILS_REQUEST = 'la/domain/sellerDetail/LOAD_REQUEST';
export const LOAD_SELLER_DETAILS_SUCCESS = 'la/domain/sellerDetail/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_SELLER_DETAILS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.sellerIds),
            };
        case LOAD_SELLER_DETAILS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_SELLER_DETAILS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.sellerDetails) {
                action.payload.sellerDetails.forEach(x => {
                    existing[x.sellerId] = { ...x };
                    loaded[x.sellerId] = time;
                    loading = difference(loading, [x.sellerId]);
                });
            }
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.sellerDetail;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

// takes in a (state, id) and returns the data
export const getSellerDetail = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

// takes in a (state, id) and returns the load time
export const getLoadTimeForSellerDetail = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

// takes in a (state, id) and returns the is loading
export const isSellerDetailLoading = createSelector([loadingSelector, idSelector], (loading, id) => {
    return loading.indexOf(id) !== -1;
});

const shouldFetchSellerDetail = (state, sellerId) => {
    const data = getSellerDetail(state, sellerId);
    if (!isEmpty(data)) {
        const loaded = getLoadTimeForSellerDetail(state, sellerId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isSellerDetailLoading(state, sellerId);
    return !loading;
};

const whichSellerDetailsNeeded = (state, sellerIds) =>
    sellerIds.filter(sellerId => shouldFetchSellerDetail(state, sellerId));

/* ACTION CREATORS */
const loadSellerDetails = sellerIds => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: sellerIds,
            type: LOAD_SELLER_DETAILS_REQUEST,
        });
        const response = await api.fetchSellerDetailsByIds({ authToken, deployment, sellerIds });
        dispatch({
            meta: { actionTime: Date.now(), sellerIds },
            payload: response.data,
            type: LOAD_SELLER_DETAILS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { sellerIds },
            payload: error,
            type: LOAD_SELLER_DETAILS_FAIL,
        });
    }
};

export const fetchSellersDetailsIfNeeded = (sellerIds: number[]) => async (dispatch: Function, getState: Function) => {
    const needed = whichSellerDetailsNeeded(getState(), sellerIds);
    if (needed.length) {
        return dispatch(loadSellerDetails(needed));
    }
    return Promise.resolve();
};
